import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Maximize2, Minimize2, ChevronLeft, ChevronRight } from 'lucide-react';

export const ResizableSplitPane = ({ 
  leftContent, 
  rightContent, 
  initialLeftWidth = 50,
  extraHeaderActions
}) => {
  const [leftWidth, setLeftWidth] = useState(initialLeftWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [collapsed, setCollapsed] = useState(null); // 'left' | 'right' | null
  const containerRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    // Clear collapsed state if dragging starts
    if (collapsed) setCollapsed(null);
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
    
    // Constrain the width between 15% and 85% to prevent complete disappearing unless explicitly collapsed
    const constrainedWidth = Math.min(Math.max(newLeftWidth, 15), 85);
    setLeftWidth(constrainedWidth);
  }, [isDragging, collapsed]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      // Disable text selection while dragging
      document.body.style.userSelect = 'none';
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const getLeftPaneWidth = () => {
    if (collapsed === 'left') return '0%';
    if (collapsed === 'right') return '100%';
    return `${leftWidth}%`;
  };

  const getRightPaneWidth = () => {
    if (collapsed === 'right') return '0%';
    if (collapsed === 'left') return '100%';
    return `${100 - leftWidth}%`;
  };

  return (
    <div 
      ref={containerRef}
      className={`flex w-full overflow-hidden transition-all duration-300 ease-in-out ${
        isFullScreen ? 'fixed inset-0 z-[100] bg-white h-screen' : 'relative flex-1 min-h-0 bg-transparent'
      }`}
    >
      {/* Fullscreen & Extra Toggle Buttons */}
      <div className={`absolute z-50 flex items-center gap-1 ${isFullScreen ? 'top-4 right-4' : 'top-2 right-2'}`}>
        {extraHeaderActions}
        <button 
          onClick={toggleFullScreen}
          className="p-1.5 bg-transparent rounded-md text-slate-400 hover:text-brand-blue hover:bg-slate-100 transition-colors"
          title={isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}
        >
          {isFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      </div>

      {/* Left Pane */}
      <div 
        className={`h-full overflow-y-auto transition-[width] duration-75 ${collapsed === 'left' ? 'hidden' : 'block'}`}
        style={{ width: getLeftPaneWidth() }}
      >
        <div className={`py-2 h-full relative ${collapsed === 'right' ? 'pr-24' : 'pr-10'}`}>
          {collapsed !== 'right' && (
            <button 
              onClick={() => setCollapsed('left')}
              className="absolute top-2 right-2 p-1.5 bg-transparent rounded-md text-slate-400 hover:text-brand-blue hover:bg-slate-100 transition-colors z-20"
              title="Collapse Left Pane"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          {/* If the right was collapsed, we need a button to restore it in the left pane */}
          {collapsed === 'right' && (
             <button 
              onClick={() => setCollapsed(null)}
              className="absolute top-2 right-14 p-1.5 bg-transparent rounded-md text-slate-400 hover:text-brand-blue hover:bg-slate-100 transition-colors z-20"
              title="Restore Summary"
            >
               <ChevronLeft size={16} />
            </button>
          )}
          {leftContent}
        </div>
      </div>

      {/* Divider */}
      {collapsed === null && (
        <div 
          className="relative flex items-center justify-center w-1 bg-slate-200 hover:bg-brand-blue/50 cursor-col-resize transition-colors group z-20"
          onMouseDown={handleMouseDown}
        >
          {/* Drag Handle Indicator */}
          <div className="absolute w-4 h-12 rounded-full bg-transparent group-hover:bg-brand-blue/20 flex items-center justify-center cursor-col-resize shadow-[inset_0_0_0_1px_transparent]">
             <div className="w-1 h-8 rounded-full bg-slate-300 group-hover:bg-brand-blue" />
          </div>
        </div>
      )}

      {/* Right Pane */}
      <div 
        className={`h-full overflow-y-auto transition-[width] duration-75 ${collapsed === 'right' ? 'hidden' : 'block'}`}
        style={{ width: getRightPaneWidth() }}
      >
        <div className="py-2 pl-10 pr-3 h-full relative">
           {collapsed !== 'left' && (
            <button 
              onClick={() => setCollapsed('right')}
              className="absolute top-2 left-2 p-1.5 bg-transparent rounded-md text-slate-400 hover:text-brand-blue hover:bg-slate-100 transition-colors z-20"
              title="Collapse Right Pane"
            >
              <ChevronRight size={16} />
            </button>
          )}
          {/* If the left was collapsed, we need a button to restore it */}
          {collapsed === 'left' && (
             <button 
              onClick={() => setCollapsed(null)}
              className="absolute top-2 left-2 p-1.5 bg-transparent rounded-md text-slate-400 hover:text-brand-blue hover:bg-slate-100 transition-colors z-20"
              title="Restore Options"
            >
               <ChevronRight size={16} />
            </button>
          )}
          {rightContent}
        </div>
      </div>
    </div>
  );
};
